/**
 * Emits a `cart_update` tracking event after a user logs in.
 *
 * On login the backend merges the guest/anonymous basket into the account
 * basket. The frontend reacts to the `LOGIN_SUCCESS` custom event; this saga
 * triggers a full (merged) basket refresh and, once it completes, emits a single
 * `cart_update` (action `merge`) carrying the whole merged basket state (full
 * product list + totals) so vendors (Bloomreach) learn about the post-merge cart.
 *
 * Kept intentionally isolated from the core basket sagas and additive: it only
 * reacts to `LOGIN_SUCCESS` and never emits on the regular add/remove/update
 * flow (that stays owned by `CartUpdateTrackingSaga`), so existing `cart_update`
 * events are unaffected.
 */
import { EventEnum } from '@as-react/common-types';
import { delay, put, race, select, take, takeEvery } from 'redux-saga/effects';
import { BasketActionTypes, GeneralActionTypes } from '../../../enums/ActionEnums';
import { emitter, EmitterEvent } from '../../../util/tracking';
import { buildCartUpdatePayload } from '../../../util/TrackingUtil';
import { basketActions } from '../actions/BasketActions.creators';
import { makeGetBasketId, makeGetBasketMappedProductsState, makeGetBasketPricingState, makeGetBasketTotalQuantityState, } from '../selectors/BasketSelector';
const REFRESH_WAIT_TIMEOUT_MS = 5000;
export function* emitLoginCartUpdates(action) {
    var _a;
    if (((_a = action.payload) === null || _a === void 0 ? void 0 : _a.eventType) !== EventEnum.LOGIN_SUCCESS) {
        return;
    }
    try {
        // A full GET is required: handleCustomEvent's login refresh uses getBasicBasket,
        // which only updates counts (basketId/totalQuantity) and never fires REFRESH_COMPLETE
        // nor loads the product details a cart_update needs. GET is throttled (2s) in
        // getBasketSaga, so a concurrent refresh is coalesced rather than duplicated.
        yield put(basketActions.createGetAction());
        // Wait for the fresh (merged) basket. Skip emission if no refresh arrives (avoids stale data).
        const { refreshed } = yield race({
            refreshed: take(BasketActionTypes.REFRESH_COMPLETE),
            timeout: delay(REFRESH_WAIT_TIMEOUT_MS),
        });
        if (!refreshed) {
            return;
        }
        const mappedProducts = yield select(makeGetBasketMappedProductsState());
        const totalQuantity = yield select(makeGetBasketTotalQuantityState());
        const pricing = yield select(makeGetBasketPricingState());
        const basketId = yield select(makeGetBasketId());
        if (!mappedProducts.length) {
            return;
        }
        // Emit a SINGLE cart_update describing the whole merged basket (full product_list
        // + totals), rather than one event per product. Use action 'merge' (not 'add') so
        // downstream can distinguish a system login-merge from a deliberate user add and
        // avoid inflating add-to-cart metrics.
        emitter.emit(EmitterEvent.CART_UPDATE, Object.assign(Object.assign({}, buildCartUpdatePayload('merge', { productId: '' }, mappedProducts, totalQuantity, pricing)), { basket_token: basketId !== null && basketId !== void 0 ? basketId : '' }));
    }
    catch (_b) {
        // Tracking must never break the login/basket flow.
    }
}
export function* loginCartUpdateTrackingSaga() {
    yield takeEvery(GeneralActionTypes.CUSTOM_EVENT, emitLoginCartUpdates);
}
